import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { Observable, map, of, switchMap, tap } from 'rxjs';

import { BaseApiService } from 'src/app/shared/services/base-api.service';
import { NotificationService } from './../../common-services/notification.service';

import {
  ClientType,
  GrpcLanguage,
  InvestorCredentialsResult,
  InvestorLoginInput,
  InvestorValidateCredentialsInput,
  LoginInput,
  LoginResultDto,
  RefreshTokenInputDto,
  RegistrationData,
  UserOTP,
  UserPassword,
  UserPhone,
  UserRegisterInput,
  ValidateCredentialsInput,   // ✅ correct spelling
} from '../../data-models/identity.data.models';

import {
  AccountDto,
  PortalCredentialsResult,
  AddSubAccountDto,
  ChangeEmailDto,
  ChangePhoneNumberDto,
  ChangeProfileImageUrlRequest,
  GetMyAccountsOutputDto,
  ProfileDataDto,
  SendOTPMailRequest,
  SendOTPPhoneRequest,
  ChangePasswordRequest,
} from '../../data-models/user.data.models';

import { GeneralResponse } from '../../data-models/shared.data-models';

/**
 * Payload returned by `register-email-otp` / `register-phone-otp`.
 * `devOtp` is populated only when the backend is running in a non-production
 * environment (controlled via `TestApi:ExposeRegistrationOtp`).
 */
export interface SendRegistrationOtpDto {
  destination?: string;
  devOtp?: string | null;
}
import { environment } from 'src/environments/environment';
import { ClientInfoDto, UpdateCompanyInput } from '../../data-models/Company-info.data.models';
import { LanguageService } from 'src/app/shared/services/language.service';

@Injectable({ providedIn: 'root' })
export class UserDataService {
  private controllerUrl: string = `${environment.appConfig.gatewayServiceBaseUrl}/api/portal/PortalAuthentication/`;
  private authcontrollerUrl: string = `${environment.appConfig.gatewayServiceBaseUrl}/api/Authentication/`;
  private usercontrollerUrl: string = `${environment.appConfig.gatewayServiceBaseUrl}/api/1/user/`;

  constructor(
    private baseApi: BaseApiService<any>,
    private notificationService: NotificationService,
    private languageService: LanguageService
  ) {}

  /** 🔹 Step 1: Generate OTP */
  GenerateUserOTP(body: { phoneNumber: string; operationType: number }): Observable<GeneralResponse<any> & { devOtp?: string }> {
    return this.baseApi.Post(`${this.controllerUrl}GenerateUserOTP`, body).pipe(
      switchMap((response) => {
        const phoneNumber = body.phoneNumber || response?.data?.phoneNumber;
        if (environment.production || !phoneNumber) {
          return of(response);
        }
        return this.GetUserDevOTP({ phoneNumber }).pipe(
          map((devRes) => {
            const devOtp = UserDataService.extractOtpFromGetUserOtpResponse(devRes);
            if (devOtp) {
              const msg =
                this.languageService.currentLanguage === 'en'
                  ? ` OTP: ${devOtp}`
                  : `رمز التحقق : ${devOtp}`;
              this.notificationService.success(msg);
            }
            return { ...response, devOtp: devOtp || undefined };
          })
        );
      })
    );
  }

  /** Normalizes `GetUserOTP` / dev OTP echo payloads (string or `{ otp }`). */
  static extractOtpFromGetUserOtpResponse(devRes: any): string {
    const raw = devRes?.resource ?? devRes?.Resource ?? devRes?.data;
    if (typeof raw === 'string') {
      return raw.trim();
    }
    if (raw && typeof raw === 'object') {
      const v = (raw as any).otp ?? (raw as any).Otp;
      return v != null ? String(v).trim() : '';
    }
    return '';
  }


  /** 🔹 Dev only */
  GetUserDevOTP(body: UserPhone): Observable<GeneralResponse<{ otp: string }>> {
    return this.baseApi.Post(`${this.usercontrollerUrl}GetUserOTP`, body);
  }

  /** 🔹 Step 2: Validate OTP */
  ValidateUserOTP(oTP: string, phoneNumber: string, language: GrpcLanguage): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Get(
      `${this.controllerUrl}ValidateUserOTP?oTP=${oTP}&phoneNumber=${encodeURIComponent(phoneNumber)}&language=${language}`
    );
  }

  /** 🔹 Step 3: Login */
  Login(body: LoginInput): Observable<GeneralResponse<LoginResultDto>> {
    return this.baseApi.Post(`${this.controllerUrl}Login`, body);
  }

  SetNewPassword(body: UserPassword): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}SetNewPassword`, body);
  }

  Register(body: RegistrationData): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(`${this.controllerUrl}Register`, body);
  }

  registerBorrower(body: RegistrationData): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(`${this.controllerUrl}register/borrower`, body);
  }

  /** Resource is the new investor's portal session, or null when the API did not open one. */
  registerInvestor(body: RegistrationData): Observable<GeneralResponse<LoginResultDto | null>> {
    return this.baseApi.Post(`${this.controllerUrl}register/investor`, body);
  }

  registerSupplier(body: RegistrationData): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(`${this.controllerUrl}register/supplier`, body);
  }
  ForgetPassword(email: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}ForgetPassword`, { email });
  }

  ValidateResetPasswordLink(
    code: string,
    userId?: string
  ): Observable<GeneralResponse<boolean>> {
    const params = new URLSearchParams();
    params.set('code', code || '');
    if (userId) {
      params.set('userId', userId);
    }
    return this.baseApi.Get(
      `${this.controllerUrl}ValidateResetPasswordLink?${params.toString()}`
    );
  }

  ResetPassword(body: {
    userId?: string;
    password: string;
    confirmPassword: string;
    code: string;
    requireChangePassword?: boolean;
  }): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}ResetPassword`, body);
  }

  InvestorRegister(body: UserRegisterInput): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(`${this.controllerUrl}InvestorRegister`, body);
  }

  InvestorCompleteRegister(transactionId: string, otp: string, phoneNumber: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}InvestorCompleteRegister`, { transactionId, otp, phoneNumber });
  }

  RefreshToken(body: RefreshTokenInputDto): Observable<GeneralResponse<{ token: string }>> {
    return this.baseApi.Post(`${this.controllerUrl}RefreshToken`, body);
  }

  Logout(): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.authcontrollerUrl}Logout`, {});
  }

  confirmEmailWithLinkCode(code: string): Observable<GeneralResponse<boolean>> {
    const url = `${this.authcontrollerUrl}confirm-email`;
    return this.baseApi.Post(url, { code });
  }

  resendInvestorEmailConfirmation(): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.authcontrollerUrl}resend-investor-email-confirmation`, {});
  }

  completeRegistrationWithOTP(body: UserOTP): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}CompleteRegistrationWithOTP`, body);
  }

  SetUserProfileComplete(): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}SetUserProfileComplete`, {});
  }

  /** 🔹 Validate user credentials before OTP */
  ValidateCredentials(body: ValidateCredentialsInput): Observable<GeneralResponse<PortalCredentialsResult>> {
    return this.baseApi.Post(`${this.controllerUrl}ValidateCredentials`, body);
  }

  /** 🔹 Investor: validate mobile number and send the login OTP (also used to resend) */
  InvestorValidateCredentials(body: InvestorValidateCredentialsInput): Observable<GeneralResponse<InvestorCredentialsResult>> {
    return this.baseApi.Post(`${this.controllerUrl}investor/ValidateCredentials`, body);
  }

  /** 🔹 Investor: verify OTP and log in */
  InvestorLogin(body: InvestorLoginInput): Observable<GeneralResponse<LoginResultDto>> {
    return this.baseApi.Post(`${this.controllerUrl}investor/login`, body);
  }

  GetProfileData(): Observable<GeneralResponse<ProfileDataDto>> {
    return this.baseApi.Get(`${this.usercontrollerUrl}GetProfileData`);
  }

  RequestAddNotificationEmail(email: string): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(`${this.controllerUrl}RequestAddNotificationEmail`, { email });
  }

  AddNotificationEmail(email: string, otp: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}AddNotificationEmail`, { email, otp });
  }

  RequestChangePhoneNumber(phone: string): Observable<GeneralResponse<string>> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json');
    return this.baseApi.Post(`${this.controllerUrl}RequestChangePhoneNumber`, JSON.stringify(`+966${phone}`), { headers });
  }

  ChangePhoneNumber(phone: string, otp: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}ChangePhoneNumber`, { phoneNumber: `+966${phone}`, otp });
  }

  ChangePassword(oldPassword: string, newPassword: string, confirmNewPassword: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}ChangePassword`, { oldPassword, newPassword, confirmNewPassword });
  }

  ChangePasswordRequest(email:string,currentPassword: string, newPassword: string, confirmNewPassword: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}ChangePassword`, { email ,currentPassword, newPassword, confirmNewPassword });
  }
  UpdateIdExpirationDate(body: any): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}UpdateIdExpirationDate`, body);
  }

  VerifyYakeenUser(nationalId: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}VerifyYakeenUser?nationalId=${nationalId}`, {});
  }

  validateInvitedUser(nationalId: string, otp: string): Observable<GeneralResponse<{ transactionId: string, phoneNumber: string }>> {
    return this.baseApi.Post(`${this.controllerUrl}ValidateInvitedUser`, { nationalId, otp });
  }

  GetMyAccounts(): Observable<GeneralResponse<AccountDto>> {
    return this.baseApi.Get(`${this.controllerUrl}GetAccountData`);
  }

  /** Change profile image after presign PUT; updated image URL is returned in GetAccountData.profileImage. */
  ChangeProfileImageUrl(request: ChangeProfileImageUrlRequest): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.usercontrollerUrl}ChangeProfileImageUrl`, request);
  }
 
  AddSubAccount(body: AddSubAccountDto): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(`${this.controllerUrl}AddSubAccount`, body);
  }

  SwitchAccount(userId: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}SwitchAccount`, { userId });
  }

  DeleteSubAccount(userId: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}DeleteSubAccount`, { userId });
  }
  verifyEmail(email: string, nid: string, clientType: ClientType, phone?: string | null): Observable<GeneralResponse<string>> {
  return this.baseApi.Post(
    `${this.controllerUrl}email/verify`,
    { email, nid, clientType, phone }
  );
  }

  /** Normalize lookup list from API (envelope or bare array; camelCase or PascalCase). */
  private static mapLookupItems(r: any): { id: string; name: string }[] {
    if (r == null) {
      return [];
    }
    if (Array.isArray(r)) {
      return UserDataService.mapLookupItemArray(r);
    }
    let raw: any = r?.data ?? r?.resource ?? r?.Data ?? r?.Resource;
    if (raw != null && !Array.isArray(raw) && typeof raw === 'object') {
      const nested =
        (raw as any).resource ??
        (raw as any).Resource ??
        (raw as any).data ??
        (raw as any).items;
      if (Array.isArray(nested)) {
        raw = nested;
      }
    }
    if (!Array.isArray(raw)) {
      return [];
    }
    return UserDataService.mapLookupItemArray(raw);
  }

  private static mapLookupItemArray(raw: any[]): { id: string; name: string }[] {
    return raw
      .map((x: any) => ({
        id: x?.id != null ? String(x.id) : x?.Id != null ? String(x.Id) : '',
        name: String(x?.name ?? x?.Name ?? '').trim(),
      }))
      .filter((x) => x.id !== '');
  }

  /** Public registration lookups (unauthenticated). */
  getCompanyTypesForRegistration(): Observable<{ id: string; name: string }[]> {
    return this.baseApi.Get(`${this.controllerUrl}company-types`).pipe(
      map((r: any) => UserDataService.mapLookupItems(r))
    );
  }

  getCompanyActivitiesForRegistration(): Observable<{ id: string; name: string }[]> {
    return this.baseApi.Get(`${this.controllerUrl}company-activities`).pipe(
      map((r: any) => UserDataService.mapLookupItems(r))
    );
  }

  /** Public nationality lookup for registration (unauthenticated). */
  getNationalitiesForRegistration(): Observable<
    { id: string; nameArabic: string; nameEnglish: string }[]
  > {
    return this.baseApi.Get(`${this.controllerUrl}nationalities`).pipe(
      map((r: any) => {
        const raw = r?.resource ?? r?.Resource ?? r?.data ?? r?.Data ?? r;
        const list = Array.isArray(raw) ? raw : [];
        return list
          .map((x: any) => ({
            id: x?.id != null ? String(x.id) : x?.Id != null ? String(x.Id) : '',
            nameArabic: String(x?.nameArabic ?? x?.NameArabic ?? '').trim(),
            nameEnglish: String(x?.nameEnglish ?? x?.NameEnglish ?? '').trim(),
          }))
          .filter((x) => x.id !== '');
      })
    );
  }

  /**
   * Sends a registration email OTP. In dev/test environments the backend echoes the
   * generated OTP in `resource.devOtp` so we surface it to the tester via a toast.
   */
  sendRegisterEmailOtp(email: string): Observable<GeneralResponse<SendRegistrationOtpDto>> {
    return this.baseApi
      .Post(`${this.controllerUrl}register-email-otp`, { email })
      .pipe(tap((res: any) => this.notifyDevOtp(res)));
  }

  verifyRegisterEmailOtp(email: string, otp: string): Observable<GeneralResponse<{ emailVerificationToken: string }>> {
    return this.baseApi.Post(`${this.controllerUrl}verify-register-email-otp`, { email, otp });
  }

  /**
   * Borrower-only: verifies that no existing borrower is registered with the given
   * National ID or phone number. Call this before `sendRegisterPhoneOtp` on Step 2
   * of the borrower registration flow. Resolves with `true` on success; throws the
   * HTTP error (with `error.message`) when the backend rejects.
   */
  validateBorrowerNidAndPhone(nid: string, phone: string, clientType: ClientType = ClientType.Borrower): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}register/borrower/validate-nid-phone`, {
      nid,
      phone,
      clientType,
    });
  }

  validateSupplierCompany(crn: string, unitedNationsNumber: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}register/supplier/validate-company`, {
      crn,
      unitedNationsNumber,
    });
  }

  validateSupplierNid(nid: string): Observable<GeneralResponse<boolean>> {
    return this.baseApi.Post(`${this.controllerUrl}register/supplier/validate-nid`, { nid });
  }

  /**
   * Sends a registration phone OTP. In dev/test environments the backend echoes the
   * generated OTP in `resource.devOtp` so we surface it to the tester via a toast.
   */
  sendRegisterPhoneOtp(phone: string, nid?: string | null, clientType?: ClientType | null): Observable<GeneralResponse<SendRegistrationOtpDto>> {
    return this.baseApi
      .Post(`${this.controllerUrl}register-phone-otp`, { phone, nid, clientType })
      .pipe(tap((res: any) => this.notifyDevOtp(res)));
  }

  verifyRegisterPhoneOtp(phone: string, otp: string): Observable<GeneralResponse<{ phoneVerificationToken: string }>> {
    return this.baseApi.Post(`${this.controllerUrl}verify-register-phone-otp`, { phone, otp });
  }

  /** Shows the dev OTP via the shared notification service when not in production. */
  private notifyDevOtp(res: any): void {
    if (environment.production) {
      return;
    }
    const devOtp: string | null | undefined =
      res?.resource?.devOtp ??
      res?.resource?.DevOtp ??
      res?.data?.devOtp ??
      res?.data?.DevOtp;
    if (!devOtp) {
      return;
    }
    const msg = this.languageService.currentLanguage === 'en'
      ? ` OTP: ${devOtp}`
      : `رمز التحقق : ${devOtp}`;
    this.notificationService.success(msg);
  }

  SendOTPMail(request: SendOTPMailRequest): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(
      `${this.controllerUrl}SendOTPMail`,
      request
    );
  }

  ChangeEmail(request: ChangeEmailDto): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(
      `${this.controllerUrl}UserChangeEmail`,
      request
    );
  }

  SendOTPPhone(request: SendOTPPhoneRequest): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(
      `${this.controllerUrl}SendOTPPhone`,
      request
    );
  }

  ChangeUserPhoneNumber(request: ChangePhoneNumberDto): Observable<GeneralResponse<string>> {
    return this.baseApi.Post(
      `${this.controllerUrl}UserChangePhoneNumber`,
      request
    );
  }
    EditCompantDetails(request: UpdateCompanyInput): Observable<GeneralResponse<ClientInfoDto>> {
    return this.baseApi.Post(
      `${this.controllerUrl}EditCompanyDetails`,
      request
    );
  }
}
